import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { Drawer, Input, Icon, Checkbox, Button, Tooltip, Badge, message, Skeleton, Modal, notification } from 'antd';
import styled from 'styled-components';
import { TableVirtuoso } from 'react-virtuoso';
import { compare } from 'natural-orderby';

import { save, fetch } from '../actions';
import { frequency as frequencyDetail } from '../helpers';
import {
  restrictsBeyondRegime,
  matchesCustomer,
  getCustomerProfile,
  hasRule,
  describeRule,
  ENROLL_IN,
  ENROLL_UNKNOWN,
} from '../enrollment';
import { department, frequency as frequencyLabel } from '~/components/RegisterTasks/Utils';
import * as calendarTasksActions from '~/_domains/calendarTasks/actions';
import { useAppContext } from '~/AppContext';

// Matriz Clientes x Tarefas: clientes nas linhas, tarefas nas colunas.
// Permite matricular (e desmatricular) vários clientes em várias tarefas
// de uma vez, sem abrir o drawer de clientes tarefa por tarefa.
//
// Performance: as linhas são virtualizadas (react-virtuoso) e as células
// usam um checkbox leve em CSS — com centenas de clientes, renderizar um
// antd Checkbox por célula travava a página.

const TASK_COL_WIDTH = 120;
const SAVE_CHUNK_SIZE = 50;
const REMOVAL_CONFIRM_THRESHOLD = 10;
// Divergência: % mínimo de clientes do regime matriculados na tarefa para
// apontar quem ficou de fora, e tamanho mínimo do regime pra estatística valer
const DIVERGENCE_RATIO = 0.8;
const DIVERGENCE_MIN_GROUP = 5;
const FILTERS_STORAGE_KEY = 'matriz_clientes_tarefas_filtros';

const DEPT_COLORS = {
  1: '#722ed1',
  2: '#1890ff',
  3: '#13c2c2',
  4: '#52c41a',
  5: '#fa8c16',
  6: '#eb2f96',
};

const TAXATION_COLORS = {
  'Simples Nacional': '#1890ff',
  'Lucro Presumido': '#722ed1',
  'Lucro Real': '#52c41a',
  'MEI': '#fa8c16',
  'Doméstica': '#eb2f96',
  'Profissional Liberal': '#13c2c2',
  'Fiscal': '#faad14',
};

const ROW_FILTERS = [
  { value: 'all', label: 'Todos' },
  { value: 'none', label: 'Sem nenhuma tarefa' },
  { value: 'dirty', label: 'Com alterações' },
  { value: 'offprofile', label: 'Fora do perfil' },
];

const loadStoredFilters = () => {
  try {
    return JSON.parse(window.localStorage.getItem(FILTERS_STORAGE_KEY)) || {};
  } catch (e) {
    return {};
  }
};

// Backgrounds via classes (não inline) para o hover de linha funcionar,
// inclusive na primeira coluna, que é sticky e precisa de fundo sólido.
// O zebra usa a classe mx-alt (índice do dado), porque com virtualização
// o nth-child do DOM não corresponde à posição real da linha.
const GridWrapper = styled.div`
  table {
    border-collapse: separate;
    border-spacing: 0;
    user-select: none;
  }

  td.mx-first { background: #fff; }
  td.mx-cell { background: #fff; }
  td.mx-cell.mx-on { background: #f0f7ff; }
  td.mx-cell.mx-warn { background: #fffbe6; }
  /* Marcada, mas o perfil do cliente nao pede: exceção, não erro. */
  td.mx-cell.mx-extra.mx-on { background: #f9f0ff; }

  tr.mx-alt td.mx-first,
  tr.mx-alt td.mx-cell { background: #fcfcfd; }
  tr.mx-alt td.mx-cell.mx-on { background: #ecf4fe; }
  tr.mx-alt td.mx-cell.mx-warn { background: #fff8db; }
  tr.mx-alt td.mx-cell.mx-extra.mx-on { background: #f4e8ff; }

  tbody tr:hover td.mx-first,
  tbody tr:hover td.mx-cell { background: #f0f5ff; }
  tbody tr:hover td.mx-cell.mx-on { background: #e2eefc; }
  tbody tr:hover td.mx-cell.mx-warn { background: #fff3bf; }
  tbody tr:hover td.mx-cell.mx-extra.mx-on { background: #efe0ff; }

  td.mx-cell:hover { box-shadow: inset 0 0 0 1px #91d5ff; }

  .mx-check {
    display: inline-block;
    width: 16px;
    height: 16px;
    border-radius: 3px;
    border: 1px solid #d9d9d9;
    background: #fff;
    position: relative;
    vertical-align: middle;
    transition: border-color 0.1s, background 0.1s;
  }
  td.mx-cell:hover .mx-check { border-color: #1890ff; }
  td.mx-cell.mx-warn .mx-check { border-color: #faad14; }
  .mx-check.on {
    background: #1890ff;
    border-color: #1890ff;
  }
  .mx-check.on:after {
    content: '';
    position: absolute;
    left: 4px;
    top: 1px;
    width: 5px;
    height: 9px;
    border: 2px solid #fff;
    border-top: 0;
    border-left: 0;
    transform: rotate(45deg);
  }
`;

const sameIdSet = (a, b) => {
  if (a.length !== b.length) return false;
  const map = {};
  for (let i = 0; i < a.length; i++) map[a[i]] = true;
  for (let j = 0; j < b.length; j++) {
    if (!map[b[j]]) return false;
  }
  return true;
};

const pillStyle = (isActive, color) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '3px 10px',
  borderRadius: 14,
  fontSize: 12,
  fontWeight: isActive ? 600 : 400,
  cursor: 'pointer',
  border: '1px solid ' + (isActive ? color : '#e8e8e8'),
  color: isActive ? color : '#8c8c8c',
  background: '#fff',
  transition: 'all 0.15s',
  whiteSpace: 'nowrap',
});

const stickyFirstCol = {
  position: 'sticky',
  left: 0,
  zIndex: 2,
  borderRight: '1px solid #f0f0f0',
};

const DrawerBulk = ({ autoOpen = false }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const { setAllOnboardingTasksAsDone } = useAppContext();

  const storedFilters = useMemo(loadStoredFilters, []);

  const [visible, setVisible] = useState(autoOpen);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState(null);
  // Edições pendentes: { customerId: [taskIds] }. A célula lê daqui quando o
  // cliente foi mexido; senão, cai no vínculo vindo do backend.
  const [edits, setEdits] = useState({});
  const [customerSearch, setCustomerSearch] = useState('');
  const [taskSearch, setTaskSearch] = useState('');
  const [dept, setDept] = useState(storedFilters.dept || 0);
  const [rowFilter, setRowFilter] = useState(storedFilters.rowFilter || 'all');
  // Regime tributário — no Brasil é o principal critério pra decidir em
  // quais tarefas um cliente entra (DAS pro Simples, ECF pro Real etc.)
  const [taxFilter, setTaxFilter] = useState(storedFilters.taxFilter || null);
  const [showDivergences, setShowDivergences] = useState(
    storedFilters.showDivergences !== undefined ? storedFilters.showDivergences : true
  );

  // Pintura por arrasto: segurar o mouse e passar pelas células aplica o
  // mesmo valor da célula inicial, como numa planilha.
  const dragRef = useRef(null);

  const tasks = useSelector(state => state.tasks.list);
  const allCustomers = useSelector(state => state.customers.list);
  const baselineRaw = useSelector(state => (state.customerTasks && state.customerTasks.data) || {});

  useEffect(() => {
    if (autoOpen) setVisible(true);
  }, [autoOpen]);

  useEffect(() => {
    if (!visible) return;
    setLoading(true);
    Promise.resolve(dispatch(fetch({ showLoading: false })))
      .catch(() => {
        message.error('Erro ao carregar os vínculos de clientes e tarefas.');
      })
      .then(() => setLoading(false));
  }, [visible]);

  useEffect(() => {
    const endDrag = () => { dragRef.current = null; };
    window.addEventListener('mouseup', endDrag);
    return () => window.removeEventListener('mouseup', endDrag);
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify({
        dept, rowFilter, taxFilter, showDivergences,
      }));
    } catch (e) { /* storage cheio/indisponível não pode quebrar a matriz */ }
  }, [dept, rowFilter, taxFilter, showDivergences]);

  const onClose = () => {
    setVisible(false);
    setEdits({});
    // Aberto via rota .../start — volta pra rota base para poder reabrir
    if (location.pathname.slice(-6) === '/start') {
      history.replace(location.pathname.slice(0, -6));
    }
  };

  const customers = useMemo(
    () => (allCustomers || [])
      .filter(c => c.enabled && c.inactived_at === null && c.is_customer)
      .slice()
      .sort((a, b) => compare()(a.cod, b.cod)),
    [allCustomers]
  );

  // Tarefas ativas em ordem alfabética — colunas previsíveis de achar
  const activeTasks = useMemo(
    () => (tasks || [])
      .filter(t => (t.status ? t.status === 'Ativa' : !t.disabled_at))
      .slice()
      .sort((a, b) => compare()(a.name, b.name)),
    [tasks]
  );

  // O backend novo devolve { customers_tasks, details }; o antigo, o mapa puro
  const baselineMap = baselineRaw.customers_tasks ? baselineRaw.customers_tasks : baselineRaw;
  const enrollDetails = baselineRaw.details || {};

  // Baseline normalizado: ids numéricos por cliente
  const baseline = useMemo(() => {
    const map = {};
    Object.keys(baselineMap).forEach(custId => {
      const list = baselineMap[custId];
      map[custId] = Array.isArray(list) ? list.map(Number) : [];
    });
    return map;
  }, [baselineMap]);

  const getCustomerTasks = (customer) => {
    if (edits[customer.id]) return edits[customer.id];
    return baseline[customer.id] || [];
  };

  const isDirty = useCallback((customer) => {
    if (!edits[customer.id]) return false;
    return !sameIdSet(edits[customer.id], baseline[customer.id] || []);
  }, [edits, baseline]);

  const dirtyCustomers = useMemo(
    () => customers.filter(isDirty),
    [customers, isDirty]
  );

  // Conjuntos efetivos por cliente para consultas rápidas nas células
  const effectiveSets = useMemo(() => {
    const map = {};
    customers.forEach(c => {
      const list = edits[c.id] || baseline[c.id] || [];
      const set = {};
      list.forEach(id => { set[id] = true; });
      map[c.id] = set;
    });
    return map;
  }, [customers, edits, baseline]);

  // Total de matrículas e remoções pendentes (para a barra de salvar)
  const pendingCounts = useMemo(() => {
    let added = 0;
    let removed = 0;
    dirtyCustomers.forEach(c => {
      const base = baseline[c.id] || [];
      const next = edits[c.id] || [];
      next.forEach(id => { if (base.indexOf(id) === -1) added++; });
      base.forEach(id => { if (next.indexOf(id) === -1) removed++; });
    });
    return { added, removed };
  }, [dirtyCustomers, edits, baseline]);

  const deptCounts = useMemo(() => {
    const counts = { 0: activeTasks.length };
    activeTasks.forEach(t => {
      const key = parseInt(t.department);
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [activeTasks]);

  const visibleTasks = useMemo(() => {
    const q = taskSearch.toLowerCase();
    return activeTasks.filter(t =>
      (dept === 0 || parseInt(t.department) === dept) &&
      (!q || t.name.toLowerCase().indexOf(q) > -1)
    );
  }, [activeTasks, dept, taskSearch]);

  const enrolledTotal = useCallback((customer) => {
    return (edits[customer.id] || baseline[customer.id] || []).length;
  }, [edits, baseline]);

  // Perfil de matrícula gravado no cliente. Onde ele existe, a matriz para de
  // adivinhar: sabe exatamente quais tarefas aquele cliente deveria cumprir.
  const customersWithoutProfile = useMemo(
    () => customers.filter(c => !getCustomerProfile(c)),
    [customers]
  );

  const rulesTasks = useMemo(() => activeTasks.filter(hasRule), [activeTasks]);

  // Divergência exata: o cliente tem perfil e está fora de uma tarefa que o
  // perfil pede ('missing'), ou dentro de uma que ele não pede ('extra').
  const offProfile = useMemo(() => {
    const map = {};

    customers.forEach(c => {
      if (!getCustomerProfile(c)) return;
      const set = effectiveSets[c.id] || {};
      const gaps = {};
      let count = 0;

      rulesTasks.forEach(t => {
        const wanted = matchesCustomer(t, c) === ENROLL_IN;
        const isOn = !!set[t.id];
        if (wanted === isOn) return;
        gaps[t.id] = wanted ? 'missing' : 'extra';
        count++;
      });

      if (count > 0) map[c.id] = gaps;
    });

    return map;
  }, [customers, rulesTasks, effectiveSets]);

  const rowFilterCounts = useMemo(() => {
    return {
      all: customers.length,
      none: customers.filter(c => enrolledTotal(c) === 0).length,
      dirty: dirtyCustomers.length,
      offprofile: Object.keys(offProfile).length,
    };
  }, [customers, enrolledTotal, dirtyCustomers, offProfile]);

  const taxationCounts = useMemo(() => {
    const counts = {};
    customers.forEach(c => {
      if (!c.type_taxation) return;
      counts[c.type_taxation] = (counts[c.type_taxation] || 0) + 1;
    });
    return counts;
  }, [customers]);

  // Estatística regime x tarefa: se quase todo mundo de um regime está numa
  // tarefa, quem ficou de fora provavelmente foi esquecido.
  const regimeStats = useMemo(() => {
    const enrolledByTask = {};
    customers.forEach(c => {
      const regime = c.type_taxation;
      if (!regime) return;
      const set = effectiveSets[c.id] || {};
      activeTasks.forEach(t => {
        if (!set[t.id]) return;
        if (!enrolledByTask[t.id]) enrolledByTask[t.id] = {};
        enrolledByTask[t.id][regime] = (enrolledByTask[t.id][regime] || 0) + 1;
      });
    });
    return enrolledByTask;
  }, [customers, effectiveSets, activeTasks]);

  const getDivergence = useCallback((customer, task, set) => {
    if (!showDivergences || set[task.id]) return null;
    // Com perfil gravado a conta é exata e vem de offProfile — a estatística
    // do regime é o que sobra para quem ainda não tem perfil.
    if (getCustomerProfile(customer)) return null;
    const regime = customer.type_taxation;
    if (!regime) return null;
    const groupTotal = taxationCounts[regime] || 0;
    if (groupTotal < DIVERGENCE_MIN_GROUP) return null;
    const enrolled = (regimeStats[task.id] && regimeStats[task.id][regime]) || 0;
    const ratio = enrolled / groupTotal;
    if (ratio < DIVERGENCE_RATIO) return null;
    return Math.round(ratio * 100);
  }, [showDivergences, taxationCounts, regimeStats]);

  const divergenceCount = useMemo(() => {
    if (!showDivergences) return 0;
    let count = 0;
    customers.forEach(c => {
      const set = effectiveSets[c.id] || {};
      activeTasks.forEach(t => {
        if (getDivergence(c, t, set) !== null) count++;
      });
    });
    Object.keys(offProfile).forEach(id => {
      count += Object.keys(offProfile[id]).length;
    });
    return count;
  }, [showDivergences, customers, activeTasks, effectiveSets, getDivergence, offProfile]);

  const visibleCustomers = useMemo(() => {
    const q = customerSearch.toLowerCase();
    return customers.filter(c => {
      if (rowFilter === 'none' && enrolledTotal(c) !== 0) return false;
      if (rowFilter === 'dirty' && !isDirty(c)) return false;
      if (rowFilter === 'offprofile' && !offProfile[c.id]) return false;
      if (taxFilter && c.type_taxation !== taxFilter) return false;
      if (!q) return true;
      return (c.name && c.name.toLowerCase().indexOf(q) > -1) ||
        (c.cod && String(c.cod).toLowerCase().indexOf(q) > -1) ||
        (c.cnpj_formated && c.cnpj_formated.toLowerCase().indexOf(q) > -1) ||
        (c.identification_number && String(c.identification_number).toLowerCase().indexOf(q) > -1);
    });
  }, [customers, customerSearch, rowFilter, taxFilter, enrolledTotal, isDirty, offProfile]);

  // Matriculados por tarefa (entre os clientes visíveis), calculado uma vez
  // por render em vez de por coluna.
  const columnCounts = useMemo(() => {
    const counts = {};
    visibleTasks.forEach(t => { counts[t.id] = 0; });
    visibleCustomers.forEach(c => {
      const set = effectiveSets[c.id];
      if (!set) return;
      visibleTasks.forEach(t => { if (set[t.id]) counts[t.id]++; });
    });
    return counts;
  }, [visibleTasks, visibleCustomers, effectiveSets]);

  const setCustomerTasks = (customer, taskIds) => {
    setEdits(prev => Object.assign({}, prev, { [customer.id]: taskIds }));
  };

  const setCellValue = (customer, taskId, value) => {
    if (saving) return;
    const current = getCustomerTasks(customer).slice();
    const index = current.indexOf(taskId);
    if (value && index === -1) current.push(taskId);
    else if (!value && index > -1) current.splice(index, 1);
    else return;
    setCustomerTasks(customer, current);
  };

  const handleCellMouseDown = (customer, task, e) => {
    if (saving) return;
    e.preventDefault();
    const set = effectiveSets[customer.id] || {};
    const target = !set[task.id];
    dragRef.current = { value: target };
    setCellValue(customer, task.id, target);
  };

  const handleCellMouseEnter = (customer, task) => {
    if (!dragRef.current || saving) return;
    setCellValue(customer, task.id, dragRef.current.value);
  };

  // Linha: matricula/remove o cliente em todas as tarefas visíveis
  const toggleRow = (customer, checked) => {
    if (saving) return;
    let current = getCustomerTasks(customer).slice();
    if (checked) {
      visibleTasks.forEach(t => {
        if (current.indexOf(t.id) === -1) current.push(t.id);
      });
    } else {
      const visibleIds = visibleTasks.map(t => t.id);
      current = current.filter(id => visibleIds.indexOf(id) === -1);
    }
    setCustomerTasks(customer, current);
  };

  // Coluna: matricula/remove todos os clientes visíveis na tarefa
  const toggleColumn = (task, checked) => {
    if (saving) return;
    setEdits(prev => {
      const next = Object.assign({}, prev);
      visibleCustomers.forEach(c => {
        const current = (next[c.id] || baseline[c.id] || []).slice();
        const index = current.indexOf(task.id);
        if (checked && index === -1) current.push(task.id);
        if (!checked && index > -1) current.splice(index, 1);
        next[c.id] = current;
      });
      return next;
    });
  };

  const discardChanges = () => setEdits({});

  // Tarefas cujo recorte vai além do regime: para elas, a matriz só decide se o
  // cliente tiver perfil gravado — o regime sozinho não responde atividade nem
  // folha.
  const tasksNeedingProfile = useMemo(
    () => rulesTasks.filter(restrictsBeyondRegime),
    [rulesTasks]
  );

  const hasAnyProfile = customersWithoutProfile.length < customers.length;

  const needsProfileNotice = tasksNeedingProfile.length > 0 && customersWithoutProfile.length > 0;

  const tasksWithRules = useMemo(
    () => rulesTasks.filter(t => !restrictsBeyondRegime(t) || hasAnyProfile),
    [rulesTasks, hasAnyProfile]
  );

  // Aplica as regras de matrícula como PENDÊNCIAS — nada é salvo até revisar.
  // Só adiciona, nunca remove. Onde há perfil gravado a decisão é completa;
  // onde não há, entra o que o regime já resolve sozinho e o resto é contado
  // como indecidível em vez de sumir sem explicação.
  const applyRules = () => {
    if (saving) return;
    if (tasksWithRules.length === 0) {
      message.info('Nenhuma tarefa tem regra aplicável por aqui. Configure em Cadastro de Tarefas > editar tarefa > "Quem recebe esta tarefa".', 6);
      return;
    }
    let applied = 0;
    let undecided = 0;
    const nextEdits = Object.assign({}, edits);
    customers.forEach(c => {
      let current = null;
      rulesTasks.forEach(t => {
        const status = matchesCustomer(t, c);
        if (status === ENROLL_UNKNOWN) {
          undecided++;
          return;
        }
        if (status !== ENROLL_IN) return;
        if (current === null) current = (nextEdits[c.id] || baseline[c.id] || []).slice();
        if (current.indexOf(t.id) === -1) {
          current.push(t.id);
          applied++;
        }
      });
      if (current !== null) nextEdits[c.id] = current;
    });
    if (applied === 0) {
      message.info('Todos os clientes já estão de acordo com as regras — nada a aplicar.');
      return;
    }
    setEdits(nextEdits);
    message.info(applied + ' matrícula(s) sugerida(s) pelas regras. Revise e clique em "Salvar alterações".', 5);
    if (undecided > 0) {
      message.warning(
        undecided + ' combinação(ões) dependem do perfil de matrícula e ficaram de fora — veja o aviso acima da matriz.',
        6
      );
    }
  };

  const clearFilters = () => {
    setCustomerSearch('');
    setTaskSearch('');
    setDept(0);
    setRowFilter('all');
    setTaxFilter(null);
  };

  const refreshAfterSave = () => {
    setAllOnboardingTasksAsDone('p6q7r8');
    dispatch(fetch({ showLoading: false }));
    dispatch(calendarTasksActions.loadAllDepartmentsCalendarTasks());
  };

  // Reaplica o estado anterior ao último save (mesmo caminho: sync por cliente)
  const undoSave = (snapshot) => {
    notification.close('matriz-save');
    setSaving(true);
    dispatch(save({ data: snapshot, sync: true }))
      .then(() => {
        message.success('Alterações desfeitas.');
        setEdits({});
        refreshAfterSave();
      })
      .catch(() => {
        message.error('Erro ao desfazer. Recarregue a matriz e confira os vínculos.');
      })
      .finally(() => setSaving(false));
  };

  const doSave = () => {
    const dirty = dirtyCustomers;
    const counts = pendingCounts;
    if (dirty.length === 0) return;

    // Guarda o estado atual pro Desfazer
    const snapshot = {};
    dirty.forEach(c => { snapshot[c.id] = (baseline[c.id] || []).slice(); });

    // Em lotes: um POST gigante com centenas de clientes arrisca timeout
    const chunks = [];
    for (let i = 0; i < dirty.length; i += SAVE_CHUNK_SIZE) {
      chunks.push(dirty.slice(i, i + SAVE_CHUNK_SIZE));
    }

    setSaving(true);
    setSaveProgress(chunks.length > 1 ? { done: 0, total: chunks.length } : null);

    let chain = Promise.resolve();
    chunks.forEach((chunk, chunkIndex) => {
      chain = chain.then(() => {
        const data = {};
        chunk.forEach(c => { data[c.id] = edits[c.id]; });
        return dispatch(save({ data, sync: true })).then(() => {
          setSaveProgress(chunks.length > 1 ? { done: chunkIndex + 1, total: chunks.length } : null);
        });
      });
    });

    chain
      .then(() => {
        setEdits({});
        refreshAfterSave();
        // Acima do drawer, senão o botão Desfazer fica encoberto
        notification.config({ zIndex: 1060 });
        notification.open({
          key: 'matriz-save',
          message: 'Alterações salvas',
          description: dirty.length + ' cliente(s) atualizados — ' +
            (counts.added > 0 ? '+' + counts.added + ' matrícula(s)' : '') +
            (counts.added > 0 && counts.removed > 0 ? ' · ' : '') +
            (counts.removed > 0 ? '−' + counts.removed + ' remoção(ões)' : '') + '.',
          duration: 10,
          btn: (
            <Button size="small" icon="undo" onClick={() => undoSave(snapshot)}>
              Desfazer
            </Button>
          ),
        });
      })
      .catch(() => {
        message.error('Erro ao salvar os vínculos. Alguns lotes podem ter sido aplicados — recarregue a matriz.');
      })
      .finally(() => {
        setSaving(false);
        setSaveProgress(null);
      });
  };

  const saveChanges = () => {
    if (dirtyCustomers.length === 0) return;
    // Remoção em massa é o acidente mais caro da tela — confirma antes
    if (pendingCounts.removed >= REMOVAL_CONFIRM_THRESHOLD) {
      Modal.confirm({
        // Acima do drawer (zIndex 1030), senão o clique não alcança o modal
        zIndex: 1060,
        title: 'Confirmar remoções em massa?',
        content: 'Você está removendo ' + pendingCounts.removed + ' vínculo(s) de tarefas' +
          (pendingCounts.added > 0 ? ' (além de adicionar ' + pendingCounts.added + ')' : '') +
          ' em ' + dirtyCustomers.length + ' cliente(s). As tarefas deixam de ser geradas no calendário para eles.',
        okText: 'Salvar mesmo assim',
        okType: 'danger',
        cancelText: 'Revisar',
        onOk: doSave,
      });
      return;
    }
    doSave();
  };

  // Exporta a visão atual (filtros aplicados) em CSV pra conferência offline
  const exportCsv = () => {
    const header = ['Código', 'Cliente', 'Regime', 'Atividade', 'Cidade', 'UF']
      .concat(visibleTasks.map(t => t.name));
    const rows = visibleCustomers.map(c => {
      const set = effectiveSets[c.id] || {};
      return [c.cod || '', c.name || '', c.type_taxation || '', c.company_type || '', c.city || '', c.state || '']
        .concat(visibleTasks.map(t => (set[t.id] ? 'X' : '')));
    });
    // BOM pro Excel abrir com acentuação correta
    const csv = String.fromCharCode(0xfeff) + [header].concat(rows)
      .map(line => line.map(v => '"' + String(v).replace(/"/g, '""') + '"').join(';'))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'matriz-clientes-tarefas.csv';
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const deptPills = [0, 1, 2, 3, 4, 5, 6].map(value => ({
    value,
    label: value === 0 ? 'Todas' : department(value),
    color: value === 0 ? '#595959' : DEPT_COLORS[value],
    count: deptCounts[value] || 0,
  }));

  const hasFilters = customerSearch || taskSearch || dept !== 0 || rowFilter !== 'all' || taxFilter;

  const tableMinWidth = 300 + visibleTasks.length * TASK_COL_WIDTH;

  // Identidade estável para o virtuoso não remontar a tabela a cada render
  const tableComponents = useMemo(() => ({
    Table: (p) => (
      <table {...p} style={Object.assign({}, p.style, { minWidth: tableMinWidth })} />
    ),
    TableRow: (p) => (
      <tr {...p} className={(p['data-index'] % 2 === 1 ? 'mx-alt' : '') + (p.className ? ' ' + p.className : '')} />
    ),
  }), [tableMinWidth]);

  const fixedHeaderContent = useCallback(() => (
    <tr>
      <th style={Object.assign({}, stickyFirstCol, {
        zIndex: 3, minWidth: 300,
        padding: '10px 14px', textAlign: 'left',
        fontSize: 12, fontWeight: 600, color: '#595959',
        borderBottom: '1px solid #f0f0f0', background: '#fafafa',
        verticalAlign: 'bottom',
      })}>
        Cliente
      </th>
      {visibleTasks.map(task => {
        const deptColor = DEPT_COLORS[parseInt(task.department)] || '#8c8c8c';
        const enrolledCount = columnCounts[task.id] || 0;
        const allChecked = visibleCustomers.length > 0 && enrolledCount === visibleCustomers.length;
        const someChecked = enrolledCount > 0 && !allChecked;
        return (
          <th key={task.id} style={{
            width: TASK_COL_WIDTH, minWidth: TASK_COL_WIDTH, maxWidth: TASK_COL_WIDTH,
            padding: '8px 6px', textAlign: 'center',
            borderBottom: '1px solid #f0f0f0', background: '#fafafa',
            verticalAlign: 'bottom',
          }}>
            <Tooltip title={
              task.name + ' · ' + department(task.department) + ' · ' +
              (task.first_date ? frequencyDetail(task.frequency, task) : frequencyLabel(task.frequency)) +
              (hasRule(task) ? ' · Regra de matrícula: ' + describeRule(task) : '')
            }>
              <div style={{
                fontSize: 11, fontWeight: 600, color: '#262626', lineHeight: '14px',
                maxHeight: 42, overflow: 'hidden', wordBreak: 'break-word',
              }}>
                {hasRule(task) && (
                  <Icon type="thunderbolt" theme="filled" style={{ color: '#faad14', fontSize: 10, marginRight: 3 }} />
                )}
                {task.name}
              </div>
            </Tooltip>
            <div style={{
              fontSize: 10, color: '#8c8c8c', fontWeight: 400, marginTop: 2,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
            }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: deptColor, flexShrink: 0 }} />
              {enrolledCount}/{visibleCustomers.length}
            </div>
            <div style={{ marginTop: 4 }}>
              <Tooltip title={allChecked
                ? 'Remover todos os clientes exibidos desta tarefa'
                : 'Matricular todos os clientes exibidos nesta tarefa'}>
                <Checkbox
                  checked={allChecked}
                  indeterminate={someChecked}
                  disabled={saving}
                  onChange={e => toggleColumn(task, e.target.checked)}
                />
              </Tooltip>
            </div>
          </th>
        );
      })}
    </tr>
  ), [visibleTasks, visibleCustomers, columnCounts, saving]);

  const itemContent = useCallback((index, customer) => {
    const set = effectiveSets[customer.id] || {};
    const dirty = isDirty(customer);
    const total = enrolledTotal(customer);
    const taxColor = TAXATION_COLORS[customer.type_taxation];
    const custDetails = enrollDetails[customer.id] || {};
    const custGaps = offProfile[customer.id] || null;
    const custProfile = getCustomerProfile(customer);
    const customerProfileLabel = custProfile ? custProfile.label : '';
    let rowChecked = 0;
    visibleTasks.forEach(t => { if (set[t.id]) rowChecked++; });
    const allRowChecked = visibleTasks.length > 0 && rowChecked === visibleTasks.length;
    const someRowChecked = rowChecked > 0 && !allRowChecked;
    const cityLine = [customer.city, customer.state].filter(Boolean).join('/');
    return (
      <React.Fragment>
        <td className="mx-first" style={Object.assign({}, stickyFirstCol, {
          padding: '5px 14px', fontSize: 12, color: '#595959',
          borderBottom: '1px solid #f5f5f5',
          maxWidth: 420, overflow: 'hidden',
        })}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Checkbox
              checked={allRowChecked}
              indeterminate={someRowChecked}
              disabled={saving}
              onChange={e => toggleRow(customer, e.target.checked)}
            />
            <div style={{ minWidth: 0, flex: 1 }} title={customer.cnpj_formated || ''}>
              <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {customer.cod && <span style={{ fontWeight: 600, marginRight: 4 }}>{customer.cod} -</span>}
                {customer.name}
                {dirty && <Badge dot style={{ marginLeft: 6 }} />}
              </div>
              {/* Dados que orientam a matrícula: regime, atividade e cidade/UF */}
              <div style={{
                fontSize: 10, color: '#8c8c8c', display: 'flex', alignItems: 'center',
                gap: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {customer.type_taxation && (
                  <span style={{ color: taxColor || '#8c8c8c', fontWeight: 600 }}>
                    {customer.type_taxation}
                  </span>
                )}
                {custProfile && (
                  <span title={'Perfil de matrícula: ' + custProfile.label} style={{ color: '#722ed1' }}>
                    · {custProfile.label}
                  </span>
                )}
                {customer.company_type && <span>· {customer.company_type}</span>}
                {cityLine && <span>· {cityLine}</span>}
                <span
                  title={total === 0
                    ? 'Cliente sem nenhuma tarefa — nada será gerado no calendário para ele'
                    : rowChecked + ' de ' + visibleTasks.length + ' tarefa(s) exibida(s)'}
                  style={{
                    color: total === 0 ? '#fa541c' : '#bfbfbf',
                    fontWeight: total === 0 ? 600 : 400,
                  }}
                >
                  · {total === 0 ? '0 tarefas' : rowChecked + '/' + visibleTasks.length}
                </span>
              </div>
            </div>
          </div>
        </td>
        {visibleTasks.map(task => {
          const isOn = !!set[task.id];
          const divergence = getDivergence(customer, task, set);
          const gap = showDivergences && custGaps ? custGaps[task.id] : null;
          const detail = isOn ? custDetails[task.id] : null;
          let cellTitle = (isOn ? 'Remover ' : 'Matricular ') + customer.name + (isOn ? ' de ' : ' em ') + task.name;
          if (detail && detail.by) {
            cellTitle += ' — matriculado por ' + detail.by + (detail.at ? ' em ' + detail.at : '');
          }
          if (gap === 'missing') {
            cellTitle = 'O perfil ' + customerProfileLabel + ' pede esta tarefa e o cliente está fora dela. ' + cellTitle;
          } else if (gap === 'extra') {
            cellTitle = 'Fora do perfil ' + customerProfileLabel + ' — pode ser exceção proposital. ' + cellTitle;
          } else if (divergence !== null) {
            cellTitle = divergence + '% dos clientes ' + customer.type_taxation +
              ' estão nesta tarefa — possível esquecimento. ' + cellTitle;
          }
          return (
            <td
              key={task.id}
              className={'mx-cell' + (isOn ? ' mx-on' : '')
                + ((divergence !== null || gap === 'missing') ? ' mx-warn' : '')
                + (gap === 'extra' ? ' mx-extra' : '')}
              title={cellTitle}
              style={{ textAlign: 'center', borderBottom: '1px solid #f5f5f5', cursor: 'pointer' }}
              onMouseDown={e => handleCellMouseDown(customer, task, e)}
              onMouseEnter={() => handleCellMouseEnter(customer, task)}
            >
              <span className={isOn ? 'mx-check on' : 'mx-check'} />
            </td>
          );
        })}
      </React.Fragment>
    );
  }, [effectiveSets, visibleTasks, saving, isDirty, enrolledTotal, getDivergence, enrollDetails, offProfile, showDivergences]);

  return (
    <Drawer
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Icon type="table" style={{ color: '#1890ff', fontSize: 18 }} />
          <span style={{ fontWeight: 600 }}>Matriz Clientes x Tarefas</span>
          <span style={{ fontSize: 12, color: '#8c8c8c', fontWeight: 400 }}>
            {visibleCustomers.length} cliente(s) · {visibleTasks.length} tarefa(s)
          </span>
        </div>
      }
      placement="right"
      onClose={onClose}
      visible={visible}
      width="95%"
      zIndex={1030}
      destroyOnClose
      bodyStyle={{ padding: '16px 20px', background: '#fafafa' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
        <Input.Search
          placeholder="Pesquisar clientes (nome, código ou CNPJ)"
          value={customerSearch}
          onChange={e => setCustomerSearch(e.target.value)}
          allowClear
          style={{ width: 280 }}
        />
        <Input.Search
          placeholder="Pesquisar tarefas"
          value={taskSearch}
          onChange={e => setTaskSearch(e.target.value)}
          allowClear
          style={{ width: 220 }}
        />
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 12, color: '#8c8c8c' }}>
          Clique ou arraste pelas células para matricular — as alterações só valem depois de salvar.
        </span>
        <Tooltip title={tasksWithRules.length > 0
          ? 'Matricular os clientes existentes conforme as regras de matrícula das tarefas (' + tasksWithRules.length + ' tarefa(s) com regra). Vira pendência pra revisar antes de salvar.'
          : 'Nenhuma tarefa tem regra de matrícula — configure ao editar a tarefa.'}>
          <Button size="small" icon="thunderbolt" onClick={applyRules} disabled={saving}>
            Aplicar regras{tasksWithRules.length > 0 ? ' (' + tasksWithRules.length + ')' : ''}
          </Button>
        </Tooltip>
        <Tooltip title="Exportar a visão atual (com filtros) em CSV">
          <Button size="small" icon="download" onClick={exportCsv}>CSV</Button>
        </Tooltip>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: '#bfbfbf', textTransform: 'uppercase', letterSpacing: 0.5 }}>Tarefas:</span>
          {deptPills.map(pill => {
            if (pill.value !== 0 && pill.count === 0) return null;
            const isActive = dept === pill.value;
            return (
              <span key={pill.value} onClick={() => setDept(pill.value)} style={pillStyle(isActive, pill.color)}>
                {pill.value !== 0 && (
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: pill.color, flexShrink: 0 }} />
                )}
                {pill.label}
                <span style={{ fontWeight: 600, opacity: 0.65 }}>{pill.count}</span>
              </span>
            );
          })}
        </div>
        <div style={{ width: 1, height: 18, background: '#e8e8e8' }} />
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: '#bfbfbf', textTransform: 'uppercase', letterSpacing: 0.5 }}>Clientes:</span>
          {ROW_FILTERS.map(f => {
            const isActive = rowFilter === f.value;
            const count = rowFilterCounts[f.value];
            if (f.value === 'dirty' && count === 0 && !isActive) return null;
            return (
              <span key={f.value} onClick={() => setRowFilter(f.value)} style={pillStyle(isActive, f.value === 'none' ? '#fa541c' : '#1890ff')}>
                {f.label}
                <span style={{ fontWeight: 600, opacity: 0.65 }}>{count}</span>
              </span>
            );
          })}
        </div>
        {Object.keys(taxationCounts).length > 0 && (
          <React.Fragment>
            <div style={{ width: 1, height: 18, background: '#e8e8e8' }} />
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: '#bfbfbf', textTransform: 'uppercase', letterSpacing: 0.5 }}>Regime:</span>
              {Object.keys(taxationCounts).sort().map(tax => {
                const isActive = taxFilter === tax;
                const color = TAXATION_COLORS[tax] || '#8c8c8c';
                return (
                  <span key={tax} onClick={() => setTaxFilter(isActive ? null : tax)} style={pillStyle(isActive, color)}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0 }} />
                    {tax}
                    <span style={{ fontWeight: 600, opacity: 0.65 }}>{taxationCounts[tax]}</span>
                  </span>
                );
              })}
            </div>
          </React.Fragment>
        )}
        <div style={{ width: 1, height: 18, background: '#e8e8e8' }} />
        <Tooltip title={'Cliente com perfil de matrícula: amarelo é tarefa que o perfil pede e falta, roxo é tarefa marcada fora do perfil. Cliente sem perfil: amarelo é estar fora de uma tarefa que ' + (DIVERGENCE_RATIO * 100) + '%+ do mesmo regime tem.'}>
          <span
            onClick={() => setShowDivergences(!showDivergences)}
            style={pillStyle(showDivergences, '#faad14')}
          >
            <Icon type="warning" style={{ fontSize: 11 }} />
            Divergências
            {showDivergences && <span style={{ fontWeight: 600, opacity: 0.65 }}>{divergenceCount}</span>}
          </span>
        </Tooltip>
      </div>

      {needsProfileNotice && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 8,
          marginBottom: 10, padding: '8px 12px',
          border: '1px solid #ffe58f', borderRadius: 6, background: '#fffbe6',
          fontSize: 12, lineHeight: 1.6, color: '#613400',
        }}>
          <Icon type="info-circle" style={{ marginTop: 3, color: '#faad14' }} />
          <span>
            <strong>{tasksNeedingProfile.length}</strong> tarefa(s) recortam por atividade ou folha e
            {' '}<strong>{customersWithoutProfile.length}</strong> cliente(s) ainda não têm perfil de
            matrícula gravado — essas combinações ficam de fora do "Aplicar regras". O perfil é
            definido na aba Regra do cliente, e depois disso a matriz já sabe o que sugerir.
          </span>
        </div>
      )}

      <GridWrapper style={{
        border: '1px solid #f0f0f0', borderRadius: 8, overflow: 'hidden',
        height: (dirtyCustomers.length > 0 ? 'calc(100vh - 265px' : 'calc(100vh - 205px')
          + (needsProfileNotice ? ' - 44px)' : ')'),
        minHeight: 320, background: '#fff',
      }}>
        {loading ? (
          <div style={{ padding: 24 }}>
            <Skeleton active paragraph={{ rows: 8 }} />
          </div>
        ) : (visibleCustomers.length === 0 || visibleTasks.length === 0) ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#8c8c8c', fontSize: 13 }}>
            <Icon type="inbox" style={{ fontSize: 32, color: '#d9d9d9', display: 'block', marginBottom: 8 }} />
            {hasFilters
              ? 'Nenhum resultado para os filtros aplicados.'
              : 'Nenhum cliente ou tarefa ativa encontrada.'}
            {hasFilters && (
              <div style={{ marginTop: 12 }}>
                <Button size="small" icon="close-circle" onClick={clearFilters}>Limpar filtros</Button>
              </div>
            )}
          </div>
        ) : (
          <TableVirtuoso
            style={{ height: '100%' }}
            data={visibleCustomers}
            components={tableComponents}
            fixedHeaderContent={fixedHeaderContent}
            itemContent={itemContent}
            overscan={10}
          />
        )}
      </GridWrapper>

      {/* Barra de salvamento — só aparece quando há alterações */}
      {dirtyCustomers.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '10px 14px', marginTop: 12,
          background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 8,
        }}>
          <Icon type="exclamation-circle" style={{ color: '#faad14' }} />
          <span style={{ flex: 1, fontSize: 13, color: '#595959' }}>
            {dirtyCustomers.length === 1
              ? <span>Alterações não salvas no cliente <strong>{dirtyCustomers[0].name}</strong></span>
              : <span><strong>{dirtyCustomers.length} clientes</strong> com alterações não salvas</span>}
            <span style={{ color: '#8c8c8c' }}>
              {' — '}
              {pendingCounts.added > 0 && <span style={{ color: '#52c41a', fontWeight: 600 }}>+{pendingCounts.added} matrícula(s)</span>}
              {pendingCounts.added > 0 && pendingCounts.removed > 0 && ' · '}
              {pendingCounts.removed > 0 && <span style={{ color: '#fa541c', fontWeight: 600 }}>−{pendingCounts.removed} remoção(ões)</span>}
            </span>
          </span>
          <Button onClick={discardChanges} disabled={saving}>Descartar</Button>
          <Button type="primary" icon="save" loading={saving} onClick={saveChanges}>
            {saving && saveProgress
              ? 'Salvando lote ' + saveProgress.done + '/' + saveProgress.total + '…'
              : 'Salvar alterações'}
          </Button>
        </div>
      )}
    </Drawer>
  );
};

export default DrawerBulk;
